import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { login, reset } from '../../features/auth/authSlice';
import { ReactComponent as LogoSiberat } from '../../svg/logo.svg';
import { Icon } from '@iconify/react';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import usePageTitle from '../../hooks/usePageTitle';

function Login() {
  usePageTitle('Login');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const { email, password } = formData;
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, isLoading, isSuccess, isError, message } = useSelector(
    state => state.auth
  );

  const onChange = e => {
    setFormData(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const handleMouseUpPassword = event => {
    event.preventDefault();
  };

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }

    if (isSuccess || user) {
      toast.success('Signed in successfully');
      navigate('/');
    }

    dispatch(reset());
  }, [isError, message, isSuccess, user, navigate, dispatch]);

  const onSubmit = e => {
    e.preventDefault();
    dispatch(login({ email, password }));
  };

  return (
    <div className='login-page min-h-screen flex flex-col'>
      <header className='mb-[35px]'>
        <div className='flex justify-between px-5 pt-5'>
          <div className='my-auto w-[50px] md:w-[75px]'>
            <LogoSiberat />
          </div>
        </div>
      </header>

      <section className='flex items-center justify-center container mx-auto mt-6 md:mt-auto content-center flex-1'>
        <div
          id='image-login'
          className='hidden md:flex flex-initial w-1/2 items-center justify-end pr-4 lg:pr-8'>
          <img
            src='/images/img-login-multimodal.png'
            alt='Pengiriman alat berat via truck dan kapal'
            className='auth-hero-img object-contain'
          />
        </div>

        <div id='form-login' className='flex-initial m-auto p-5 max-w-[440px] w-full'>
          <div id='title' className='flex flex-col'>
            <h1 className='text-[32px] md:text-[35px] font-bold text-[#1B2124] leading-tight m-0'>
              Welcome back
            </h1>
          </div>
          <p
            id='desc'
            className='text-left text-sm my-3 font-normal text-[#6B7280]'>
            Sign in to your SiBerat Customer account to continue.
          </p>

          <form onSubmit={onSubmit} autoComplete='off' className='text-sm'>
            <label
              className='font-medium block mb-1.5 mt-6 text-[#4F4F4F]'
              htmlFor='email'>
              Email
            </label>
            <OutlinedInput
              className='input-login w-full'
              id='email'
              name='email'
              value={email}
              onChange={onChange}
              autoFocus
              placeholder='you@company.com'
              required
              type='email'
              size='small'
              startAdornment={
                <InputAdornment position='start'>
                  <Icon
                    icon='solar:letter-bold'
                    width={18}
                    color='#888888'
                  />
                </InputAdornment>
              }
            />

            <label
              className='font-medium block mb-1.5 mt-6 text-[#4F4F4F]'
              htmlFor='password'>
              Password
            </label>
            <OutlinedInput
              className='input-login w-full'
              placeholder='Enter your password'
              type={showPassword ? 'text' : 'password'}
              size='small'
              required
              id='password'
              name='password'
              onChange={onChange}
              startAdornment={
                <InputAdornment position='start'>
                  <Icon
                    icon='solar:lock-password-bold'
                    width={18}
                    color='#888888'
                  />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={
                      showPassword ? 'hide the password' : 'display the password'
                    }
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge='end'
                    className='show-password'>
                    <Icon
                      icon={
                        showPassword
                          ? 'solar:eye-closed-bold'
                          : 'solar:eye-bold'
                      }
                      width={18}
                      color='#6D6D6D'
                    />
                  </IconButton>
                </InputAdornment>
              }
            />

            <button
              className='btn-primary-siberat w-full h-12 py-3 px-4 mt-8 focus:outline-none'
              type='submit'>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>

            <div className='text-center mt-5'>
              <NavLink
                to='/forgot-password'
                className='inline-flex items-center gap-1.5 font-semibold text-[#BD0E0E]'>
                <Icon icon='solar:key-bold' width={16} />
                Forgot password?
              </NavLink>
            </div>
          </form>
        </div>
      </section>

      <footer className='mt-auto px-5 py-5'>
        <div className='font-medium text-xs text-[#9CA3AF]'>
          &copy; {new Date().getFullYear()} PT. SiBerat Digital Logistik, All
          Rights Reserved.
        </div>
      </footer>
    </div>
  );
}

export default Login;
